import QuestionField = namaTypes.QuestionField;
import DTOFilledQuestionField = namaTypes.DTOFilledQuestionField;
import NaMaMetaData = namaTypes.NaMaMetaData;
import FieldMetaData = namaTypes.FieldMetaData;
import ReportMetadata = namaTypes.ReportMetadata;
import EntityReferenceData = namaTypes.EntityReferenceData;
import TitledID = namaTypes.TitledID;
import FieldActionContextInfo = namaTypes.FieldActionContextInfo;
import DTOCriteria = namaTypes.DTOCriteria;
import {ref, Ref} from "vue";
import {WidgetsPresenter} from "@/utils/presenters/interfaces.ts";
import ActionPerformer from "@/utils/moreMenu/ActionPerformer.ts";
import ExpressionEditUtility from "@/utils/common/ExpressionEditUtility.ts";
import FieldTypesUtils from "@/utils/common/FieldTypesUtils.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import {useErrorsStore} from "@/stores/ToastDataStore.ts";
import Translator from "@/utils/common/Translator.ts";
import NamaPlace from "@/utils/presenters/NamaPlace.ts";
import BaseDispatcher from "@/utils/presenters/BaseDispatcher.ts";
import GenericValues from "@/utils/common/GenericValues.ts";
import CriteriaUtils from "@/utils/criteria/CriteriaUtils.ts";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import TreeViewPresenter from "@/utils/presenters/TreeViewPresenter.ts";
import HierarchyFilterHelper from "@/utils/common/HierarchyFilterHelper.ts";
import TreeModelProvider from "@/utils/common/TreeModelProvider.ts";
import CommonConstants from "@/utils/constants/CommonConstants.ts";
import DomainBaseEntities from "@/utils/constants/DomainBaseEntities.ts";
import ListPresenter from "@/utils/common/ListPresenter.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import {EditPresenter} from "@/utils/presenters/EditPresenter.ts";
import EditScreenFieldLayout from "@/utils/constants/EditScreenFieldLayout.ts";
import GUIUtilities from "@/utils/common/GUIUtilities.ts";

export class QuestionFieldsPresenter implements WidgetsPresenter {
  questionFields?: QuestionField[];
  record: Ref<Record<string, any>> = ref({});
  defaultValues?: DTOFilledQuestionField[];
  disabledQuestionFields: string[] = [];
  reportMetadata?: ReportMetadata;
  reportRef?: EntityReferenceData;
  private _performer?: ActionPerformer;
  private _ownerId?: string;
  private _place?: NamaPlace;
  isBulkUpdating: boolean = false;
  searchOwnerEntityType?: string;
  static readonly detailId: string = "$details$";

  fetchFieldState(fieldId: string): string | undefined | null {
    return null;
  }

  addDisabledQuestionFieldId(fieldId: string) {
    this.disabledQuestionFields.push(fieldId);
  }

  isFieldDisabled(fieldId: string): boolean {
    return false;
  }

  isColumnDisabled(rowId: string, parentDetailId: string, columnId: string): boolean {
    return this.disabledQuestionFields && this.disabledQuestionFields.contains(columnId);
  }

  isFieldHidden(field: string): boolean {
    return false;
  }

  fetchColumnState(rowId: string, parentDetailId: string, columnId: string): string | undefined | null {
    return null;
  }

  constructor(ownerId?: string) {
    this._ownerId = ownerId;
  }

  async edit(questionFields?: QuestionField[], defaultValues?: DTOFilledQuestionField[], hidden: string[] = []) {
    this.questionFields = questionFields ?? [];
    this.defaultValues = defaultValues ?? [];

    if (this.questionFields) {
      for (let questionField of this.questionFields) {
        if (!questionField.id)
          continue;
        if (hidden.contains(questionField.id) || questionField.hidden)
          questionField.hidden = true;
        if (questionField.list)
          this.record.value[questionField.id] = {[QuestionFieldsPresenter.detailId]: []};
        if (this.defaultValues[questionField.id])
          this.record.value[questionField.id] = FieldTypesUtils.parseString(this.defaultValues[questionField.id].objValue, questionField.fieldType);
        if (questionField.metaData && questionField.metaData['defaultValue']) {
          let defaultValue = (questionField.metaData as FieldMetaData).defaultValue;
          let parsedDefaultValue = ExpressionEditUtility.getParsedValue(questionField, defaultValue, []);
          if (!this.isDroppableEnumDefault(questionField, parsedDefaultValue))
            this.record.value[questionField.id] = parsedDefaultValue;
        } else if (questionField.metaData?.fieldType == FieldType.Boolean) {
          this.record.value[questionField.id] = false;
        }
        let defaultValue: DTOFilledQuestionField | undefined = this.getDefaultValue(questionField, this.defaultValues);
        if (defaultValue) {
          let inValues: string[] = defaultValue.value ? [defaultValue.value] : [];
          if (questionField.list) {
            let strs: string[] = defaultValue.value ? StringUtils.csvLineToList(defaultValue.value, CommonConstants.LIST_PARAMS_SEP) : [];
            let details: any [] = []
            for (let str of strs) {
              let parsedValue: any = ExpressionEditUtility.getParsedValue(questionField, str, inValues);
              details.push({
                id: parsedValue.id ?? await ClientCache.asyncNextGUIDId(),
                [questionField.id]: parsedValue
              });
            }
            defaultValue.objValue = details;
            this.record.value[questionField.id][QuestionFieldsPresenter.detailId] = defaultValue.objValue;
          } else {
            defaultValue.objValue = ExpressionEditUtility.getParsedValue(questionField, defaultValue.value, inValues);
            this.setParsedValue(questionField, defaultValue.objValue);
          }
        }
        if (questionField.metaData && questionField.metaData.fieldType === FieldType.Reference) {
          let targetEntity = questionField.metaData.referencedEntityType;
          // if (targetEntity&&targetEntity.startsWith("$"))
          //   this.registerDynamicSelector(targetEntity.substring(1));
          if (!this.record.value[ListPresenter.EXTRA_CRITERIA])
            this.record.value[ListPresenter.EXTRA_CRITERIA] = {};
          this.record.value[ListPresenter.EXTRA_CRITERIA][questionField.id] = this.fetchCriteriaFromFilterField(targetEntity, questionField.id);
        }
        if (this.reportMetadata && this.reportMetadata.questionsChangeUtil) {
          // TODO:: setValidator
        }
      }
    }
    this.updateDynamicSelectors();
  }

  private setParsedValue(questionField: namaTypes.QuestionField, parsedStringValue: any) {
    if (!questionField.id)
      return;
    if (this.isDroppableEnumDefault(questionField, parsedStringValue))
      return;
    this.record.value[questionField.id] = parsedStringValue;
  }

  private isDroppableEnumDefault(questionField: QuestionField, value: any): boolean {
    const fieldType = questionField.fieldType ?? questionField.metaData?.fieldType;
    if (fieldType !== FieldType.Enum && fieldType !== FieldType.EntityType)
      return false;
    const allowedValues = this.getAllowedValuesForField(questionField);
    return allowedValues.length > 0 && !allowedValues.includes(value);
  }

  private getAllowedValuesForField(questionField: QuestionField): string[] {
    if (questionField.allowedValues && questionField.allowedValues.length)
      return questionField.allowedValues.map(av => av.id ?? av.value ?? "");
    return GUIUtilities.getAllowedValues(this.entityType, questionField.metaData ?? {});
  }

  getDefaultValue(questionField: QuestionField, defaultValues: DTOFilledQuestionField[]): DTOFilledQuestionField | undefined {
    if (defaultValues) {
      for (let defaultValue of defaultValues) {
        if (defaultValue.id === questionField.id)
          return defaultValue;
      }
    }
    return;
  }

  isAnyRequiredFieldOrGroupEmpty(): boolean {
    let retValue: boolean = false;
    let notFilledRequiredFields: QuestionField[] = this.getNotFilledRequiredFields();
    if (notFilledRequiredFields && notFilledRequiredFields.length) {
      for (let field of notFilledRequiredFields) {
        let id = Translator.translateTitledId("", field as TitledID) ?? "";
        useErrorsStore().pushErrorStr(Translator.translateMessage("pleaseInputRequiredParameters", id));
      }
      retValue = true;
    }
    let notFilledRequiredGroups: string[] = this.getNotFilledRequiredGroups();
    if (notFilledRequiredGroups && notFilledRequiredGroups.length) {
      for (let group of notFilledRequiredGroups) {
        useErrorsStore().pushErrorStr(Translator.translateMessage("pleaseInputAtLeastOneOfRequiredGroupParametersWithGroupName", group));
      }
      retValue = true;
    }
    return retValue;
  }

  getNotFilledRequiredFields(): QuestionField[] {
    let requiredFields: QuestionField[] = [];
    if (!this.questionFields)
      return requiredFields;
    let answers: DTOFilledQuestionField[] = this.getQuestionFields();
    for (let q of this.questionFields) {
      if (q.required) {
        let answer: DTOFilledQuestionField | undefined = answers.find(l => l.id === q.id);
        if (!answer?.value?.trim()) {
          requiredFields.push(q);
        }
      }
    }
    return requiredFields;
  }

  private getNotFilledRequiredGroups() {
    let requiredFields: string[] = [];
    let requiredGroups: Record<string, QuestionField[]> = {};
    let requiredGroupsWithFilledValues: string[] = [];
    if (!this.questionFields)
      return requiredFields;
    let answers: DTOFilledQuestionField[] = this.getQuestionFields();
    for (let q of this.questionFields) {
      if (q.requiredGroup) {
        if (requiredGroups[q.requiredGroup])
          requiredGroups[q.requiredGroup].push(q)
        else
          requiredGroups[q.requiredGroup] = [q];
        let answer: DTOFilledQuestionField | undefined = answers.find(l => l.id === q.id);
        if (answer?.value?.trim()) {
          requiredGroupsWithFilledValues.push(q.requiredGroup);
        }
      }
    }
    for (let group in requiredGroups) {
      if (requiredGroupsWithFilledValues.contains(group))
        continue;
      requiredFields.push(group);
    }
    return requiredFields;
  }

  private updateDynamicSelectors(): void {
    let questionFields: DTOFilledQuestionField[] = this.getQuestionFields();
    // for (let dynamicSelector : dynamicSelectors)
    // {
    //
    // }
  }

  getQuestionFields(): DTOFilledQuestionField[] {
    let filledQuestionFields: DTOFilledQuestionField[] = [];
    if (this.questionFields) {
      for (let questionField of this.questionFields) {
        if (questionField.id) {
          let value: any = NamaCommon.getFieldValue(this.fetchRecord(), questionField.id);
          if (ObjectChecker.isNullOrUndefined(value)) {
            filledQuestionFields.push({id: questionField.id});
            continue;
          }
          if (value === '""')
            value = '';
          let stringValue: string;
          if (questionField.list) {
            let details: any[] = value[QuestionFieldsPresenter.detailId] ?? [];
            if (!details)
              continue;
            let fieldValues: string[] = [];
            for (let detail of details) {
              let lineValue: any = detail instanceof Object ? detail[questionField.id] : detail;
              if (ObjectChecker.isNullOrUndefined(lineValue))
                continue;
              fieldValues.push(FieldTypesUtils.toString(questionField.metaData?.fieldType, lineValue));
            }
            stringValue = StringUtils.toCSVLineWithSep(CommonConstants.LIST_PARAMS_SEP, fieldValues)
          } else {
            stringValue = FieldTypesUtils.toString(questionField.metaData?.fieldType, value);
          }
          let filledQuestionField: DTOFilledQuestionField = {
            id: questionField.id,
            value: stringValue,
            objValue: value
          };
          filledQuestionFields.push(filledQuestionField);
        }
      }
    }
    return filledQuestionFields;
  }


  get ownerId(): string {
    return this._ownerId ?? '';
  }

  set ownerId(value: string) {
    this._ownerId = value;
  }

  get place(): NamaPlace | undefined {
    return this._place;
  }

  set place(value: NamaPlace | undefined) {
    this._place = value;
  }

  get performer(): ActionPerformer | undefined {
    return this._performer;
  }

  set performer(value: ActionPerformer) {
    this._performer = value;
  }

  createSearchContextInfo(fieldId: string, lineNumber?: number): FieldActionContextInfo {
    let searchContextInfo: FieldActionContextInfo = {};
    fieldId = fieldId.split('.')[0];
    if (this.performer && this.performer.getSearchContextProvider()) {
      searchContextInfo = this.fetchDispatcher().createSearchContextInfo(fieldId, -1);
    } else {
      searchContextInfo.sourceFieldId = fieldId;
      searchContextInfo.sourceEntityType = this.searchOwnerEntityType ?? DomainBaseEntities.ReportDefinition;
    }
    let qf: QuestionField | undefined = this.questionFields?.find(f => f.id === fieldId);
    if (qf)
      searchContextInfo.filteringParams = qf.fieldFilteringParams;
    searchContextInfo.contextFields = {};
    for (let questionField of this.getQuestionFields()) {
      if (!questionField.id)
        continue;
      let field: QuestionField | undefined = this.questionFields ? this.questionFields.find(l => l.id == questionField.id) : undefined;
      if (field && field.id && field.list && BaseDispatcher.commonContextFields.contains(field.id))
        continue;
      searchContextInfo.contextFields[questionField.id] = GenericValues.createGenericValueBasedOnTheValue(questionField.id, questionField.objValue, questionField.value);
    }
    if (!searchContextInfo.sourceFieldValue)
      searchContextInfo.sourceFieldValue = searchContextInfo.contextFields[fieldId];
    return searchContextInfo;
  }

  get entityType(): string {
    return this.ownerId;
  }

  getDefaultCriteria(fieldId: string): DTOCriteria | undefined {
    if (fieldId.includes("." + QuestionFieldsPresenter.detailId))
      fieldId = fieldId.substring(0, fieldId.indexOf("." + QuestionFieldsPresenter.detailId));
    return this.fetchRecord()[ListPresenter.EXTRA_CRITERIA] && this.fetchRecord()[ListPresenter.EXTRA_CRITERIA][fieldId] ? this.fetchRecord()[ListPresenter.EXTRA_CRITERIA][fieldId] : undefined;
  }

  fetchNodeIdFromPlace(): string | undefined {
    return undefined;
  }

  setTreeView(treeViewPresenter: TreeViewPresenter): void {

  }

  onTreeNodeSelection(): void {

  }

  updateSelectedNodeId(value: string | undefined | null): void {
  }

  getHelperForTreeView(): HierarchyFilterHelper | undefined {
    return undefined;
  }

  getTreeModelProviderForTreeView(): TreeModelProvider | undefined {
    return undefined;
  }

  isValidFieldForAttach(field: any): boolean {
    return true;
  }

  fetchDispatcher(): BaseDispatcher {
    return this.performer?.getSearchContextProvider()!;
  }

  fetchMetadata(): NaMaMetaData {
    return {};
  }

  fetchPlace(): NamaPlace | undefined {
    return this.place;
  }

  fetchQuestionFields(): QuestionField[] {
    return this.questionFields ?? [];
  }

  fetchRecord(): Record<string, any> {
    let myRecord = this.record.value;
    if (!myRecord)
      myRecord = this.record as Record<string, any>;
    return myRecord;
  }

  fetchFieldErrorMsg(): string {
    return ''
  }

  fetchCriteriaFromPlace(): namaTypes.DTOCriteria | undefined {
    return undefined;
  }

  fetchExtraCriteriaIdFromPlace(): string | undefined {
    return undefined;
  }

  fetchParentFieldTitle(): string {
    return '';
  }

  isEditPresenter(): this is EditPresenter {
    return false;
  }

  doesFieldHasSuggestionProvider(fieldMetaData: FieldMetaData): boolean {
    return false;
  }

  fetchCriteriaFromFilterField(filteredType?: string, fieldId?: string): namaTypes.DTOCriteria | undefined {
    if (!fieldId)
      return undefined;
    let field = this.questionFields?.find(l => l.id === fieldId)
    if (!field)
      return undefined;
    let expressions = CriteriaUtils.criteriaStrToMap(field.criteriaExpression)
    let strExpression;
    if (filteredType)
      strExpression = expressions[filteredType];
    if (!strExpression)
      strExpression = expressions[""];
    if (!strExpression)
      return undefined;
    let referencesToOtherFields: string[] = StringUtils.getAllInnerStrings(strExpression, "${", "}");
    for (const refToField of referencesToOtherFields) {
      strExpression = strExpression.replace("${" + refToField + "}", this.valueCalculatorForQuestionField(refToField));
    }
    return CriteriaUtils.criteriaFromStr(strExpression);
  }

  valueCalculatorForQuestionField(ref: string) {
    const fields = this.getQuestionFields().filter(field => field.id === ref);
    if (fields && fields.length)
      return fields[0].value;
    return "";
  }


  static async setQuestionListValue(object: Record<string, any>, path: string, list: any[]) {
    let details: any[] = [];
    let columnId = path.split('.')[0];
    for (let value of list) {
      let str: string = value instanceof Object ? value[columnId] : value;
      details.push({
        id: await ClientCache.asyncNextGUIDId(),
        [columnId]: str
      });
    }
    NamaCommon.setFieldValue(object, path, details);
  }

  static createSampleQuestionFields():QuestionField[] {
    let sampleQuestionFields: QuestionField[] = [];
    sampleQuestionFields.push(NamaCommon.createQuestionField("text1", FieldType.Text));
    sampleQuestionFields.push(NamaCommon.createQuestionField("text2", FieldType.Text));
    sampleQuestionFields.push(NamaCommon.createQuestionField("text3", FieldType.Text));
    sampleQuestionFields.push(NamaCommon.createQuestionField("text4", FieldType.Text));
    sampleQuestionFields.push(NamaCommon.createQuestionField("text5", FieldType.BigText, EditScreenFieldLayout.spanned));
    return sampleQuestionFields;
  }
}
